import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm, useWatch } from "react-hook-form";
import { useFormContext } from "../../../context/Context";
import Breadcrumb from '../../../components/layout/Breadcrumb';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useHolidayListContext } from '../../../context/HolidayListContext';

export default function ApplyLeave() {
    const navigate = useNavigate();
    const {
        register, handleSubmit, onSubmit, reset, formState: { errors }, clearErrors, setValue, getValues, control
    } = useForm();
    const {setServerError} = useFormContext();
    const [selectApplyLeaveFromDateColor, setApplyLeaveFromDateColor] = useState("#d3d3d3");
    const [selectApplyLeaveToDateColor, setApplyLeaveToDateColor] = useState("#d3d3d3");
    const [selectApplyLeaveDateColor, setApplyLeaveDateColor] = useState("#d3d3d3");
    const [selectLeaveTypeColor, setLeaveTypeColor] = useState("#d3d3d3");
    const [selectLeaveDurationColor, setLeaveDurationColor] = useState("#d3d3d3");
    const [selectApplyLeaveReportingManagerColor, setApplyLeaveReportingManagerColor] = useState("#d3d3d3");
    const [leaveDuration, setLeaveDuration] = useState("full");
    const [fullDayLeaveData, setFullDayLeaveData] = useState({});
    const [halfDayLeaveData, setHalfDayLeaveData] = useState({});
    const [minToDate, setMinToDate] = useState("");

    const holidays = useHolidayListContext();

    // 1. Get the watched values
    const watchedValues = useWatch({
        control,
        name: ["applyLeaveFromDate", "applyLeaveToDate", "applyLeaveType", "applyLeaveReason", "applyLeaveReportingManager", "applyLeaveDate", "applyHalfDayLeaveDuration"],
    });


    const [fromDate, toDate, leaveType, reason, reportingManager, halfDayDate, applyHalfDayLeaveDuration] = watchedValues;


    const isHalfDay = leaveDuration === "half";

    const isApplyLeaveDisabled = isHalfDay
        ? !leaveType || !reason?.trim() || !reportingManager || !halfDayDate || !applyHalfDayLeaveDuration
        : !leaveType || !reason?.trim() || !reportingManager || !fromDate || !toDate;

    const isCancelLeaveDisabled = isHalfDay
        ? !(leaveType || reason?.trim() || reportingManager || halfDayDate || applyHalfDayLeaveDuration)
        : !(leaveType || reason?.trim() || reportingManager || fromDate || toDate);

    const today = new Date().toISOString().split("T")[0];

    // Convert holiday dates to YYYY-MM-DD format
    const formattedHolidays = useMemo(() => {
        const monthMap = {
            Jan: "01", Feb: "02", Mar: "03", Apr: "04", May: "05", Jun: "06",
            Jul: "07", Aug: "08", Sep: "09", Oct: "10", Nov: "11", Dec: "12",
        };
        const year = new Date().getFullYear();

        // Using a Set here
        return new Set(holidays.map((holiday) => {
            const [month, day] = holiday.date.split(" ");
            return `${year}-${monthMap[month]}-${day.padStart(2, "0")}`;
        }));
    }, [holidays]);

    // Function to check if a date is a holiday or weekend
    const isDisabledFromDate = (date) => {
        const day = new Date(date).getDay(); // 0 = Sunday, 6 = Saturday
        return formattedHolidays.has(date) || day === 0 || day === 6;
    };

    const handleApplyLeaveFromDateColorChange = (e) => {
        const selectedApplyLeaveFromDateValue = e.target.value;
        setMinToDate(selectedApplyLeaveFromDateValue);
        if (isDisabledFromDate(selectedApplyLeaveFromDateValue)) {
            alert("This date is a holiday or a weekend. Please select another date.");
            setValue("applyLeaveFromDate", ""); // Use setValue to update the form value
            setApplyLeaveFromDateColor("#d3d3d3");
        } else {
            setApplyLeaveFromDateColor("black");
            setValue("applyLeaveToDate", ""); // Reset "To" date when "From" date changes
            setApplyLeaveToDateColor("#d3d3d3");
        }
        clearErrors("applyLeaveFromDate");
    };
    const handleApplyLeaveToDateColorChange = (e) => {
        const selectedApplyLeaveToDateValue = e.target.value;
        if (isDisabledFromDate(selectedApplyLeaveToDateValue)) {
            alert("This date is a holiday or a weekend. Please select another date.");
            setValue("applyLeaveToDate", "");
            setApplyLeaveToDateColor("#d3d3d3");
        } else {
            setApplyLeaveToDateColor("black");
        }
        clearErrors("applyLeaveToDate");
    };
    const handleApplyLeaveDateColorChange = (e) => {
        const selectedApplyLeaveDateValue = e.target.value;
        if (isDisabledFromDate(selectedApplyLeaveDateValue)) {
            alert("This date is a holiday or a weekend. Please select another date.");
            setValue("applyLeaveDate", "");
            setApplyLeaveDateColor("#d3d3d3");
        } else {
            setValue("applyLeaveDate", selectedApplyLeaveDateValue);
            setApplyLeaveDateColor("black");
        }
        clearErrors("applyLeaveDate");
    };
    const handleLeaveTypeColorChange = (e) => {
        const selectedLeaveTypeValue = e.target.value;
        setValue("applyLeaveType", selectedLeaveTypeValue);
        setLeaveTypeColor(selectedLeaveTypeValue ? "black" : "#d3d3d3");
        clearErrors('applyLeaveType');
    };
    const handleLeaveDurationColorChange = (e) => {
        const selectedLeaveDurationValue = e.target.value;
        setValue("applyHalfDayLeaveDuration", selectedLeaveDurationValue);
        setLeaveDurationColor(selectedLeaveDurationValue ? "black" : "#d3d3d3");
        clearErrors("applyHalfDayLeaveDuration");
    };
    const handleApplyLeaveReportingManagerColorChange = (e) => {
        const selectedApplyLeaveReportingManagerValue = e.target.value;
        setValue("applyLeaveReportingManager", selectedApplyLeaveReportingManagerValue);
        setApplyLeaveReportingManagerColor(selectedApplyLeaveReportingManagerValue ? "black" : "#d3d3d3");
        clearErrors('applyLeaveReportingManager');
    };
    // for changing the leave duration mode
    const handleLeaveDurationChange = (type) => {
        if (type === "half") {
            // Preserve current full day data before switching
            const currentFullDayData = {
                applyLeaveType: getValues("applyLeaveType"),
                applyLeaveReason: getValues("applyLeaveReason"),
                applyLeaveReportingManager: getValues("applyLeaveReportingManager"),
                applyLeaveNotifyManager: getValues("applyLeaveNotifyManager"),
            };
            setFullDayLeaveData(currentFullDayData);
            // Restore previously saved half day data
            setValue("applyLeaveDate", halfDayLeaveData.applyLeaveDate || "");
            setValue("halfDayLeaveDuration", halfDayLeaveData.applyHalfDayLeaveDuration || "");
            setValue("applyLeaveType", halfDayLeaveData.applyLeaveType || "");
            setValue("applyLeaveReason", halfDayLeaveData.applyLeaveReason || "");
            setValue("applyLeaveReportingManager", halfDayLeaveData.applyLeaveReportingManager || "");
            setValue("applyLeaveNotifyManager", halfDayLeaveData.applyLeaveNotifyManager || "");
            setLeaveTypeColor(halfDayLeaveData.applyLeaveType ? "black" : "#d3d3d3");
            setApplyLeaveReportingManagerColor(halfDayLeaveData.applyLeaveReportingManager ? "black" : "#d3d3d3");
            setLeaveDurationColor(halfDayLeaveData.applyHalfDayLeaveDuration ? "black" : "#d3d3d3");
        } else if (type === "full") {
            // Preserve current half day data before switching
            const currentHalfDayData = {
                applyLeaveDate: getValues("applyLeaveDate"),
                leaveDuration: getValues("applyHalfDayLeaveDuration"),
                applyLeaveType: getValues("applyLeaveType"),
                applyLeaveReason: getValues("applyLeaveReason"),
                applyLeaveReportingManager: getValues("applyLeaveReportingManager"),
                applyLeaveNotifyManager: getValues("applyLeaveNotifyManager"),
            };
            setHalfDayLeaveData(currentHalfDayData);
            // Restore previously saved full day data
            setValue("applyLeaveType", fullDayLeaveData.applyLeaveType || "");
            setValue("applyLeaveReason", fullDayLeaveData.applyLeaveReason || "");
            setValue("applyLeaveReportingManager", fullDayLeaveData.applyLeaveReportingManager || "");
            setValue("applyLeaveNotifyManager", fullDayLeaveData.applyLeaveNotifyManager || "");
            setLeaveTypeColor(fullDayLeaveData.applyLeaveType ? "black" : "#d3d3d3");
            setApplyLeaveReportingManagerColor(fullDayLeaveData.applyLeaveReportingManager ? "black" : "#d3d3d3");
        }
        setLeaveDuration(type);
    };
    const handleLeaveCancel = () => {
        reset();
        setValue("applyLeaveFromDate", "");
        setValue("applyLeaveToDate", "");
        setApplyLeaveReportingManagerColor("#d3d3d3");
        setLeaveTypeColor("#d3d3d3");
        setApplyLeaveToDateColor("#d3d3d3");
        setApplyLeaveFromDateColor("#d3d3d3");
        setLeaveDurationColor("#d3d3d3");
        clearErrors();
    };
    // for showing the options conditionally
    const leaveTypeOptions =
        leaveDuration === 'full' ? ["Earn Leave (5)", "Sick Leave (2)", "Loss Of Pay Leave (0)", "Maternity Leave (2)", "Marriage Leave (2)"] :
            ["Earn Leave (5)", "Sick Leave (2)", "Loss Of Pay Leave (0)"];
    const leaveDurationOptions =
        ["First Half Day", "Second Half Day"];

    const handleFormSubmit = (data) => {
        setLeaveDuration((prevDuration) => {
            const formData = {
                ...data,
                leaveDuration: prevDuration
            };
            onSubmit(formData);
            reset({
                applyLeaveType: '',
                applyLeaveReason: '',
                applyLeaveReportingManager: '',
                applyLeaveNotifyManager: '',
                applyHalfDayLeaveDuration: '',
                applyLeaveDate: '',
                applyLeaveFromDate: '',
                applyLeaveToDate: ''
            });
            setTimeout(() => {
                navigate('/admin/dashboard');
            }, 2000);
            const toastMessage = (
                <div>
                    <strong>Leave applied successfully!</strong>
                    <div>{data.applyLeaveNotifyManager ? "Your reporting manager has been notified" : ""}</div>
                </div>
            );
            setValue("applyLeaveFromDate", "");
            setValue("applyLeaveToDate", "");
            setServerError('');
            clearErrors();
            toast.success(toastMessage);
            setApplyLeaveReportingManagerColor("#d3d3d3");
            setLeaveTypeColor("#d3d3d3");
            setApplyLeaveToDateColor("#d3d3d3");
            setApplyLeaveFromDateColor("#d3d3d3");
            setApplyLeaveDateColor("#d3d3d3");
            setLeaveDurationColor("#d3d3d3");
            return prevDuration;
        });
    };

    return (
        <div className='container-fluid'>
            <div className='row clearfix'>
                <div className='col-md-12'>
                    <Breadcrumb />
                    <div className='attendance-container' style={{ padding: '0', marginBottom: '45px', marginTop: '35px' }}>
                        <form onSubmit={handleSubmit(handleFormSubmit)}>
                            <h4 className='apply-leave-heading'>Apply Leave</h4>
                            <div className='leave-type'>
                                <button className={`full-day-btn ${leaveDuration === 'full' ? "active" : ""}`}
                                    style={{ background: leaveDuration === 'full' ? '#76157B' : 'darkgray', color: 'white' }} onClick={() => handleLeaveDurationChange("full")}
                                    type='button' >Full Day</button>
                                <button className={`half-day-btn ${leaveDuration === 'half' ? "active" : ""}`}
                                    style={{ background: leaveDuration === 'half' ? '#76157B' : 'darkgray', color: 'white' }} onClick={() => handleLeaveDurationChange("half")} type='button'>Half Day</button>
                            </div>
                            <div className='apply-leave-section'>
                                {leaveDuration === "full" ? (
                                    <>
                                        <div className="input-text">
                                            <p className='applyLeaveLabel'>From <span style={{ color: "red" }}>*</span></p>
                                            <div className="user-input-icons">
                                                <input className="input-field" type="date"
                                                    min={today}
                                                    {...register("applyLeaveFromDate", {
                                                        required: true,
                                                    })}
                                                    onChange={(e) => {
                                                        // Let React Hook Form handle the change first
                                                        register("applyLeaveFromDate").onChange(e);

                                                        // Run the logic
                                                        handleApplyLeaveFromDateColorChange(e);
                                                    }}
                                                    style={{ color: selectApplyLeaveFromDateColor, padding: '5px' }} />
                                                {errors.applyLeaveFromDate && (<div className="userErrorMessage">{errors.applyLeaveFromDate.message}</div>)}
                                            </div>
                                        </div>
                                        <div className="input-text">
                                            <p className='applyLeaveLabel'>To <span style={{ color: "red" }}>*</span></p>
                                            <div className="user-input-icons">
                                                <input className="input-field" type="date" min={minToDate}
                                                    {...register("applyLeaveToDate", {
                                                        required: true,
                                                    })}
                                                    onChange={(e) => {
                                                        register("applyLeaveToDate").onChange(e);
                                                        handleApplyLeaveToDateColorChange(e);
                                                    }}
                                                    style={{ color: selectApplyLeaveToDateColor, padding: '5px' }} />
                                                {errors.applyLeaveToDate && (<div className="userErrorMessage">{errors.applyLeaveToDate.message}</div>)}
                                            </div>
                                        </div>
                                    </>
                                ) : (
                                    <>
                                        <div className="input-text">
                                            <p className='applyLeaveLabel'>Date<span style={{ color: "red" }}>*</span></p>
                                            <div className="user-input-icons">
                                                <input className="input-field" type="date" min={today}
                                                    {...register("applyLeaveDate", {
                                                        required: true,
                                                    })} onChange={(e) => handleApplyLeaveDateColorChange(e)} style={{ color: selectApplyLeaveDateColor, padding: '5px' }} />
                                                {errors.applyLeaveDate && (<div className="userErrorMessage">{errors.applyLeaveDate.message}</div>)}
                                            </div>
                                        </div>
                                        <div className="input-text">
                                            <p className='applyLeaveLabel'>Leave Duration <span style={{ color: "red" }}>*</span></p>
                                            <div className="user-input-icons">
                                                <select className="input-field" {...register("applyHalfDayLeaveDuration", { required: true })}
                                                    onChange={(e) => handleLeaveDurationColorChange(e)} style={{ color: selectLeaveDurationColor }}>
                                                    <option value="" hidden style={{ color: "#d3d3d3" }}>Choose Your Leave Duration</option>
                                                    {leaveDurationOptions.map((option) => (
                                                        <option key={option.id} style={{ color: 'black' }}>{option}</option>
                                                    ))}
                                                </select>
                                                {errors.applyHalfDayLeaveDuration && (<div className="userErrorMessage">{errors.applyHalfDayLeaveDuration.message}</div>)}
                                            </div>
                                        </div>
                                    </>
                                )}
                                <div className="input-text">
                                    <p className='applyLeaveLabel'>Leave Type <span style={{ color: "red" }}>*</span></p>
                                    <div className="user-input-icons">
                                        <select className="input-field" {...register("applyLeaveType", { required: true })}
                                            onChange={(e) => handleLeaveTypeColorChange(e)} style={{ color: selectLeaveTypeColor }}>
                                            <option value="" hidden style={{ color: "#d3d3d3" }}>Choose your leave type</option>
                                            {leaveTypeOptions.map((option) => (
                                                <option key={option} style={{ color: 'black' }}>{option}</option>
                                            ))}
                                        </select>
                                        {errors.applyLeaveType && (<div className="userErrorMessage">{errors.applyLeaveType.message}</div>)}
                                    </div>
                                </div>
                                <div className="input-text" style={{ marginTop: '5px' }}>
                                    <p className='applyLeaveLabel'>Reason For Leave <span style={{ color: "red" }}>*</span></p>
                                    <div className="user-input-icons">
                                        <textarea rows="4" className="input-field" {...register("applyLeaveReason", { required: true })}>
                                        </textarea>
                                        {errors.applyLeaveReason && (<div className="userErrorMessage">{errors.applyLeaveReason.message}</div>)}
                                    </div>
                                </div>
                                <div className="input-text" style={{ marginTop: '-6px' }}>
                                    <p className='applyLeaveLabel'>Reporting Manager <span style={{ color: "red" }}>*</span></p>
                                    <div className="user-input-icons">
                                        <select className="input-field" {...register("applyLeaveReportingManager", { required: true })}
                                            onChange={(e) => handleApplyLeaveReportingManagerColorChange(e)} style={{ color: selectApplyLeaveReportingManagerColor }}>
                                            <option value="" hidden style={{ color: "#d3d3d3" }}>Choose Your Reporting Manager</option>
                                            <option style={{ color: 'black' }}>Anup Mangla</option>
                                            <option style={{ color: 'black' }}>Shaurya</option>
                                        </select>
                                        {errors.applyLeaveReportingManager && (<div className="userErrorMessage">{errors.applyLeaveReportingManager.message}</div>)}
                                    </div>
                                </div>
                                <div className="input-text" style={{ marginTop: '-5px' }}>
                                    <div className="user-input-checkbox-icons">
                                        <label>
                                            <input className="input-field" type="checkbox" style={{ width: '15px', marginRight: '0' }}{...register("applyLeaveNotifyManager")} />
                                            <span>Notify Your Manager</span>
                                        </label>
                                        {errors.applyLeaveNotifyManager && (<div className="userErrorMessage">{errors.applyLeaveNotifyManager.message}</div>)}
                                    </div>
                                </div>
                                <div className='edit-delete-btn-container'>
                                    <button className="primary-btn" style={{ background: 'darkgray', width: '100px' }} disabled={isCancelLeaveDisabled} onClick={() => handleLeaveCancel()}>Cancel</button>
                                    <button className="primary-btn" type="submit" style={{ width: '100px' }} disabled={isApplyLeaveDisabled} >Apply</button>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
            <ToastContainer />
        </div >
    )
}
